import {
  BookOpenCheck,
  CalendarDays,
  GraduationCap,
  PartyPopper,
  Star,
  Sun,
  Users,
  type LucideIcon,
} from 'lucide-react';

import { SCHOOL_LEVEL_LABEL, type Option } from '@/constants/institute';
import { EVENT_CATEGORIES } from './schemas/event.schema';
import type {
  CalendarAudience,
  CalendarClassScope,
  CalendarEventCategory,
  CalendarItemCategory,
} from './types/calendar';

/**
 * The kinds of thing a school year is made of, and how each one is drawn.
 *
 * One table rather than a colour decided at each of the seven places a category
 * appears — the tile, the filter chip, the month cell, the week column, the ring
 * in the year grid, the legend and the badge on the rail. Seven near-identical
 * blocks of markup is how "Exams" ends up blue in one view and indigo in
 * another (CLAUDE.md §3).
 *
 * The hues are semantic rather than the console's blue throughout: a year read
 * at a glance is read by colour, so a holiday and an exam term have to be
 * tellable apart across a twelve-month grid. Exams take the console blue because
 * they are the spine of an academic year; everything else sits around it.
 */
export interface CategoryStyle {
  /** Singular, for a legend row or the badge on one entry. */
  label: string;
  /** Plural, for a figure tile and a filter chip. */
  short: string;
  icon: LucideIcon;
  /** The chip a single event wears in the grid, the roster and the rail. */
  chip: string;
  /** The mark beside it in a legend, and inside a day cell. */
  dot: string;
  /** The ring a day wears in the year grid when this kind falls on it. */
  ring: string;
  /** The tile's own tint, matching `StatCard`'s variants. */
  variant: 'rose' | 'indigo' | 'emerald' | 'amber' | 'cyan' | 'orange' | 'teal';
}

export const CATEGORY: Record<CalendarItemCategory, CategoryStyle> = {
  EXAM: {
    label: 'Examination',
    short: 'Examinations',
    icon: GraduationCap,
    chip: 'bg-blue-50 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-900/60',
    dot: 'bg-blue-500',
    ring: 'border-blue-500 text-blue-700 dark:text-blue-300',
    variant: 'indigo',
  },
  HOLIDAY: {
    label: 'Holiday',
    short: 'Holidays',
    icon: Sun,
    chip: 'bg-rose-50 dark:bg-rose-950/50 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-900/60',
    dot: 'bg-rose-500',
    ring: 'border-rose-500 text-rose-700 dark:text-rose-300',
    variant: 'rose',
  },
  EVENT: {
    label: 'Event',
    short: 'Events',
    icon: PartyPopper,
    chip: 'bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-900/60',
    dot: 'bg-emerald-500',
    ring: 'border-emerald-500 text-emerald-700 dark:text-emerald-300',
    variant: 'emerald',
  },
  MEETING: {
    label: 'Meeting / PTM',
    short: 'Meetings',
    icon: Users,
    chip: 'bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-900/60',
    dot: 'bg-amber-500',
    ring: 'border-amber-500 text-amber-700 dark:text-amber-300',
    variant: 'amber',
  },
  IMPORTANT: {
    label: 'Important',
    short: 'Important dates',
    icon: Star,
    chip: 'bg-orange-50 dark:bg-orange-950/50 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-900/60',
    dot: 'bg-orange-500',
    ring: 'border-orange-500 text-orange-700 dark:text-orange-300',
    variant: 'orange',
  },
  ACADEMIC: {
    label: 'Academic activity',
    short: 'Academic activities',
    icon: BookOpenCheck,
    chip: 'bg-cyan-50 dark:bg-cyan-950/50 text-cyan-700 dark:text-cyan-300 border-cyan-200 dark:border-cyan-900/60',
    dot: 'bg-cyan-500',
    ring: 'border-cyan-500 text-cyan-700 dark:text-cyan-300',
    variant: 'cyan',
  },
  VACATION: {
    label: 'Vacation break',
    short: 'Vacations',
    icon: CalendarDays,
    chip: 'bg-violet-50 dark:bg-violet-950/50 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-900/60',
    dot: 'bg-violet-500',
    ring: 'border-violet-500 text-violet-700 dark:text-violet-300',
    variant: 'teal',
  },
};

/** The order the year is read in — the spine first, then what sits around it. */
export const CATEGORY_ORDER: CalendarItemCategory[] = [
  'EXAM',
  'HOLIDAY',
  'EVENT',
  'MEETING',
  'IMPORTANT',
  'ACADEMIC',
  'VACATION',
];

/**
 * The figures the year is summed up by — one tile per kind the "Add event"
 * menu offers, in the menu's order, so every kind an office can add has a count
 * and nothing is counted that cannot be added here. Built from
 * `EVENT_CATEGORIES` for that reason; holidays follow, as they do in the menu.
 * Vacations are left out: they are declared through the holiday form too, and a
 * seventh tile would be a count nobody asked for.
 */
export const HEADLINE_CATEGORIES: CalendarItemCategory[] = [...EVENT_CATEGORIES, 'HOLIDAY'];

/**
 * The kinds the form offers — `CATEGORY_ORDER` minus the two the holidays table
 * owns. Built from `EVENT_CATEGORIES` so the list cannot drift from what the
 * server will accept (CLAUDE.md §3).
 */
export const CATEGORY_OPTIONS: Option<CalendarEventCategory>[] = EVENT_CATEGORIES.map((value) => ({
  value,
  label: CATEGORY[value].label,
}));

export const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** Column headings for the month grid. */
export const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** The same seven, for a mini month where a three-letter day will not fit. */
export const WEEKDAY_INITIALS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

export const AUDIENCE_LABEL: Record<CalendarAudience, string> = {
  EVERYONE: 'Everyone',
  STUDENTS: 'Students',
  TEACHERS: 'Teachers',
  PARENTS: 'Parents',
};

export const AUDIENCE_OPTIONS: Option<CalendarAudience>[] = (
  Object.keys(AUDIENCE_LABEL) as CalendarAudience[]
).map((value) => ({ value, label: AUDIENCE_LABEL[value] }));

/**
 * Which classes an entry touches.
 *
 * The bands are `SCHOOL_LEVEL_LABEL`'s rather than retyped, so a band renamed on
 * the institute profile is renamed here too (CLAUDE.md §3). `K12` is left out
 * because `ALL_CLASSES` already says the whole school, and the server's enum
 * does not carry it — offering it would be a select that saves nothing.
 */
export const CLASS_SCOPE_LABEL: Record<CalendarClassScope, string> = {
  ALL_CLASSES: 'All classes',
  NO_CLASSES: 'No classes',
  PRE_PRIMARY: SCHOOL_LEVEL_LABEL.PRE_PRIMARY,
  PRIMARY: SCHOOL_LEVEL_LABEL.PRIMARY,
  UPPER_PRIMARY: SCHOOL_LEVEL_LABEL.UPPER_PRIMARY,
  SECONDARY: SCHOOL_LEVEL_LABEL.SECONDARY,
  SENIOR_SECONDARY: SCHOOL_LEVEL_LABEL.SENIOR_SECONDARY,
};

export const CLASS_SCOPE_OPTIONS: Option<CalendarClassScope>[] = (
  Object.keys(CLASS_SCOPE_LABEL) as CalendarClassScope[]
).map((value) => ({ value, label: CLASS_SCOPE_LABEL[value] }));

/** What every figure tile on this screen is counted over. */
export const TILE_HINT = 'This academic year';


/**
 * `2026-27` → `Academic year 2026-27`.
 *
 * The session's own `name` from the database rather than one worked out from a
 * date: it is the label the institute already prints on its documents, and an
 * institute whose year is written differently should read its own wording here.
 */
export function academicYearLabel(name: string): string {
  return `Academic year ${name}`;
}
